import logService from '../services/wss/logService3';
import GeoScopeIdBuilder from './GeoScopeIdBuilder';
import JwtClaimsReader from './JwtClaimsReader';

const _key = 'jerny_tab_id';

let _tabId = null;
let _uid = null;
let _token = null;
let _unsubscribeListener = null;
let _currentTab = null;
let _activeGroup = null;
let _workspaceRk = null; // null = default workspace

/**
 * Waits for Clerk to be ready AND session to be available using addListener.
 * addListener fires immediately if Clerk is already loaded — no polling needed.
 * Falls back after 5s to avoid blocking forever if user is not signed in.
 * @returns {Promise<void>}
 */
const _waitForClerkReady = () =>
    new Promise((resolve) => {
        if (window.Clerk?.session) return resolve();

        let unsub = null;
        const timeout = setTimeout(() => {
            unsub?.();
            resolve();
        }, 5000);

        unsub = window.Clerk?.addListener(({ session }) => {
            if (session) {
                clearTimeout(timeout);
                unsub?.();
                resolve();
            }
        });
    });

/**
 * App-wide context — tab ID, user ID and token in one central place.
 * Plain module, no React dependency. Call init() once on app start from PageContent.
 */
const AppContext = {
    /**
     * Initializes tabId, uid and token. Called once from PageContent.componentDidMount.
     * Sets up Clerk session listener to keep token fresh.
     * Clerk refreshes the token automatically every 50s — addListener picks that up.
     * TabId is generated and persisted in localStorage if not present.
     */
    async init() {
        await _waitForClerkReady();

        let id = localStorage.getItem(_key);
        if (!id) {
            id = crypto.randomUUID();
            localStorage.setItem(_key, id);
        }
        _tabId = id;
        _uid = window.Clerk?.user?.id ?? null;

        // Fetch token — await directly so _token is guaranteed set before init() returns
        _token = await window.Clerk?.session?.getToken() ?? null;

        logService.log(`AppContext.init: session=${!!window.Clerk?.session} token=${!!_token}`);

        // Keep token fresh via Clerk listener — Clerk auto-refreshes every 50s
        _unsubscribeListener = window.Clerk?.addListener(({ session }) => {
            session?.getToken().then((t) => {
                _token = t;
            });
        });
    },

    /**
     * Releases the Clerk session listener. Call on logout or app teardown.
     */
    destroy() {
        _unsubscribeListener?.();
        _unsubscribeListener = null;
        _token = null;
        _uid = null;
        _tabId = null;
        _currentTab = null;
        _activeGroup = null;
        _workspaceRk = null;
    },

    /**
     * Returns the current tab ID, or null if init has not been called yet.
     * @returns {string|null}
     */
    getTabId() {
        return _tabId;
    },

    /**
     * Sets the active workspace for window geo scoping, null = default workspace.
     * @param {string|null} rk
     */
    setWorkspaceRk(rk) {
        _workspaceRk = rk;
    },

    /**
     * Window geo scope of the active workspace, see getGeoScopeIdFor.
     * @returns {string|null}
     */
    getGeoScopeId() {
        return this.getGeoScopeIdFor(_workspaceRk);
    },

    /**
     * Window geo scope: per browser AND per workspace, GUID-shaped (BE requirement).
     * Default workspace (null) keeps the plain tabId so existing saved positions stay valid.
     * @param {string|null} wsRk
     * @returns {string|null}
     */
    getGeoScopeIdFor(wsRk) {
        if (!_tabId || !wsRk) return _tabId;
        return GeoScopeIdBuilder.build(_tabId, wsRk);
    },

    /**
     * Returns the current Clerk user ID, or null if init has not been called yet.
     * @returns {string|null}
     */
    getUid() {
        return _uid;
    },

    async fetchToken() {
        logService.log(`AppContext.fetchToken: awaiting token`);
        _token = await window.Clerk?.session?.getToken({ skipCache: true });
        logService.log(`AppContext.fetchToken: awaiting token finished`);
        return _token;
    },

    /**
     * Returns the cached Clerk session token — synchronous, no async needed.
     * Token is kept fresh via Clerk's own 50s refresh + addListener.
     * @returns {string|null}
     */
    getToken() {
        return _token;
    },

    /**
     * True when the session token carries role 'admin' — same check as TopicSearch (sessionClaims.role).
     * UI-only, the BE does its own authorization.
     * @returns {boolean}
     */
    isAdmin() {
        return JwtClaimsReader.read(_token)?.role === 'admin';
    },

    /**
     * Sets the current active tab name. Called by each tab on mount.
     * @param {string} tab
     */
    setCurrentTab(tab) {
        _currentTab = tab;
    },

    /**
     * Returns the current active tab name, or null if not set.
     * @returns {string|null}
     */
    getCurrentTab() {
        return _currentTab;
    },

    /**
     * Sets the active topic group name. Only set from YourNotesTab.
     * @param {string|null} groupName
     */
    setActiveGroup(groupName) {
        _activeGroup = groupName;
    },

    /**
     * Returns the active topic group name, or null if not set.
     * @returns {string|null}
     */
    getActiveGroup() {
        return _activeGroup;
    },
};

export default AppContext;
